/**
 * Revenue Analytics Page for Master Admin
 * Displays platform-wide revenue metrics and trends
 */

import { useState, useEffect } from 'react'
import Card from '../components/Card'
import Button from '../components/Button'
import { BarChart, LineChart, PieChart } from '../components/Chart'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  RefreshCw,
  Download,
} from 'lucide-react'
import {
  getPlatformRevenueAnalytics,
  getRevenueTrends,
} from '../services/revenueAnalytics'
import { exportToCSV, exportToExcel } from '../services/exportService'

const RevenueAnalytics = () => {
  const [analytics, setAnalytics] = useState(null)
  const [trends, setTrends] = useState(null)
  const [timeRange, setTimeRange] = useState(30)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [timeRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const [analyticsData, trendsData] = await Promise.all([
        getPlatformRevenueAnalytics(timeRange),
        getRevenueTrends(90),
      ])

      if (analyticsData.success) {
        setAnalytics(analyticsData.data)
      }
      if (trendsData.success) {
        setTrends(trendsData.data)
      }
    } catch (error) {
      console.error('Error loading revenue data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (format) => {
    if (!analytics || !trends) return

    const exportData = [
      {
        Metric: 'Total Organizations',
        Value: analytics.totalOrganizations,
      },
      {
        Metric: 'Active Subscriptions',
        Value: analytics.activeSubscriptions,
      },
      {
        Metric: 'MRR',
        Value: `$${analytics.mrr.toLocaleString()}`,
      },
      {
        Metric: 'ARR',
        Value: `$${analytics.arr.toLocaleString()}`,
      },
      {
        Metric: 'New Organizations',
        Value: analytics.newOrganizations,
      },
      {
        Metric: 'Churned Organizations',
        Value: analytics.churnedOrganizations,
      },
      ...trends.map((t) => ({
        Month: t.month,
        Revenue: `$${t.revenue.toLocaleString()}`,
        'New Organizations': t.newOrganizations,
        'Churned Organizations': t.churnedOrganizations,
      })),
    ]

    if (format === 'csv') {
      exportToCSV(exportData, `revenue_analytics_${new Date().toISOString().split('T')[0]}.csv`)
    } else {
      await exportToExcel(exportData, `revenue_analytics_${new Date().toISOString().split('T')[0]}.xlsx`)
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading revenue analytics...</p>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="section-container">
        <p className="text-gray-500">No revenue data available</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <DollarSign className="w-8 h-8" />
          Revenue Analytics
        </h1>
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="px-4 py-2 border rounded-lg"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('xlsx')}>
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Recurring Revenue</p>
              <p className="text-3xl font-bold">${analytics.mrr.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Annual Recurring Revenue</p>
              <p className="text-3xl font-bold">${analytics.arr.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Subscriptions</p>
              <p className="text-3xl font-bold">{analytics.activeSubscriptions}</p>
            </div>
            <Building2 className="w-8 h-8 text-purple-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New Organizations</p>
              <p className="text-3xl font-bold text-green-600">{analytics.newOrganizations}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <Card>
        <h2 className="text-xl font-semibold mb-4">Revenue by Subscription Tier</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">Trial</p>
            <p className="text-2xl font-bold">${analytics.revenueByTier.trial.toLocaleString()}</p>
            <p className="text-sm text-gray-500">{analytics.subscriptionBreakdown.trial} orgs</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600">White Label</p>
            <p className="text-2xl font-bold">
              ${analytics.revenueByTier.white_label.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">
              {analytics.subscriptionBreakdown.white_label} orgs
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Demo</p>
            <p className="text-2xl font-bold">${analytics.revenueByTier.demo.toLocaleString()}</p>
            <p className="text-sm text-gray-500">{analytics.subscriptionBreakdown.demo} orgs</p>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends */}
        {trends && trends.length > 0 && (
          <Card>
            <h2 className="text-xl font-semibold mb-4">Revenue Trends (90 days)</h2>
            <LineChart
              data={trends.map((t) => ({
                month: t.month,
                revenue: t.revenue,
              }))}
              xKey="month"
              yKey="revenue"
              title="Monthly Revenue"
            />
          </Card>
        )}

        {/* Subscription Breakdown */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Subscription Breakdown</h2>
          <PieChart
            data={[
              { tier: 'Trial', count: analytics.subscriptionBreakdown.trial },
              { tier: 'White Label', count: analytics.subscriptionBreakdown.white_label },
              { tier: 'Demo', count: analytics.subscriptionBreakdown.demo },
            ]}
            labelKey="tier"
            valueKey="count"
          />
        </Card>
      </div>

      {/* Growth Metrics */}
      <Card>
        <h2 className="text-xl font-semibold mb-4">Growth Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-600 mb-1">New Organizations</p>
            <p className="text-2xl font-bold text-green-600">{analytics.newOrganizations}</p>
            <p className="text-xs text-gray-500">Last {timeRange} days</p>
          </div>
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Churned Organizations</p>
            <p className="text-2xl font-bold text-red-600">{analytics.churnedOrganizations}</p>
            <p className="text-xs text-gray-500">Last {timeRange} days</p>
          </div>
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Net Growth</p>
            <p
              className={`text-2xl font-bold ${
                analytics.newOrganizations - analytics.churnedOrganizations >= 0
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {analytics.newOrganizations - analytics.churnedOrganizations > 0 ? '+' : ''}
              {analytics.newOrganizations - analytics.churnedOrganizations}
            </p>
            <p className="text-xs text-gray-500">Last {timeRange} days</p>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default RevenueAnalytics

